import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import * as apiSearch from 'api/status/PostStatusSearchV1';
import Search from 'classes/StandingData/Status/Search/Search';
import Status from 'classes/StandingData/Status/Search/Status';
import StatusList from 'classes/StandingData/Status/Search/StatusList';
import StatusDetailComponent from 'components/desktop/StandingData/Status/Detail/StatusDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList from 'components/desktop/_general/StandingDataList/StandingDataList';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useAirNavigation from 'hooks/useAirNavigation';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const StatusManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	const dragItem = useRef<number | null>();
	const dragOverItem = useRef<number | null>();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { params } = useAirNavigation();
	const displayOverlay = params.get('id') ? true : false;

	const [statusList, setStatusList] = useState<StatusList>(new StatusList());
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			await getStatus();
		} else {
			await getStatus();
		}
		setSearch(clone(search));
	};

	const getStatus = async () => {
		setIsLoading(true);
		try {
			const response: apiSearch.ResponseData | null = await statusList.callApi(
				search.mapToRequest()
			);
			if (response) {
				statusList.mapFromApi(response);
				setStatusList(clone(statusList));
			}
		} finally {
			setIsLoading(false);
		}
	};

	const getSuggestedStatus = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await statusList.search(searchTerm, props.archive ?? false);
		} else {
			statusList.filtered = [];
		}

		setStatusList(clone(statusList));
		return statusList.filtered;
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getStatus();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		handleChangeId(null);
	};

	const handleArchiveStatus = async (status: Status): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:ARCHIVE_TITLE'),
			text: t('standingData:STATUS_ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await status.callApiDelete();
				await Swal.fire({
					title: t('standingData:STATUS_ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getStatus();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleRestoreStatus = async (status: Status): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:RESTORE_TITLE'),
			text: t('standingData:STATUS_RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await status.callApiRestore();
				await Swal.fire({
					title: t('standingData:STATUS_RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getStatus();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
	};

	const onDragStart = (_e: any, position: number) => {
		dragItem.current = position;
	};

	const onDragEnter = (_e: any, position: number) => {
		dragOverItem.current = position;
	};

	const onDragEnd = (_e: any) => {
		const dragItemContent = statusList.all[Number(dragItem.current)];

		statusList.all.splice(Number(dragItem.current), 1);
		statusList.all.splice(Number(dragOverItem.current), 0, dragItemContent);

		getPositions();

		dragItem.current = null;
		dragOverItem.current = null;

		statusList.callApiPutList();
		setStatusList(clone(statusList));
	};

	const getPositions = () => {
		statusList.all.forEach((s: Status, index: number) => {
			s.order = index;
		});
	};

	const getButtonDataArchive = (input: Status): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleArchiveStatus(input),
			};
		}
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleOverlayOpen(id),
			};
		}
	};

	const getButtonDataRestore = (input: Status): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreStatus(input),
			};
		}
	};

	const renderButtons = (input: Status): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderListRows = () => {
		return statusList.all.map((s: Status, index: number) => {
			return {
				id: s.id,
				jsx: (
					<div
						className="d-flex flex-row justify-content-start align-items-center"
						style={{ gap: '2em' }}
						onDragOver={(e) => e.preventDefault()}
						onDragStart={(e) => onDragStart(e, index)}
						onDragEnter={(e) => onDragEnter(e, index)}
						onDragEnd={onDragEnd}
						key={index}
						draggable
					>
						<div className="flex-fill">{s.name}</div>
						<div>{renderButtons(s)}</div>
					</div>
				),
			};
		});
	};

	const renderContent = () => {
		return (
			<>
				<main className="container">
					<div className="d-flex flex-row justify-content-between">
						{!props.archive ? (
							<>
								<h1>{t('standingData:STATUS_MANAGEMENT')}</h1>
								<div className="d-flex flex-gap-25">
									<BtnNew
										onClick={handleChangeIdToNew}
										name={t('standingData:CREATE_STATUS')}
									/>
								</div>
							</>
						) : (
							<h1>{t('standingData:STATUS_ARCHIVE')}</h1>
						)}
					</div>
					<br />
					<div className="row">
						<div className="col-lg-12">
							<div className="input-group mb-3">
								<AdvancedSearchInput
									debounceMilliSeconds={350}
									getSuggestedItems={getSuggestedStatus}
									handleSearch={handleSearch}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									triggerSearchLetterAmount={1}
								/>
							</div>
						</div>
					</div>
					<br />
					<LoadingAnimation isLoading={isLoading} />
					<p>{`${statusList.totalCount} ${t('_general:STATUS')}`}</p>
					<StandingDataList
						archive={props.archive}
						displayOverlay={displayOverlay}
						rows={renderListRows()}
						overlay={
							<StatusDetailComponent
								highestOrder={Math.max(
									...statusList.all.map((s: Status) => s.order)
								)}
							/>
						}
						handleOverlayOpen={handleOverlayOpen}
						handleOverlayClose={handleOverlayClose}
						draggable={true}
					/>
					<br />
					<Pagination
						countOfAllListItems={statusList.totalCount}
						pageIndex={search.getPageIndex()}
						setPageIndex={handleChangePageIndex}
						listItemAmount={search.getPageSize()}
						maxNumberOfButtons={maxNumberOfButtons}
					/>
				</main>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default StatusManagementComponent;
